import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { servicesApi } from "../services/api";
import HeroSection from "../components/HeroSection";

function ConsultingPage() {
  const [focusAreas, setFocusAreas] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [processSteps, setProcessSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsultingData();
  }, []);

  const fetchConsultingData = async () => {
    setLoading(true);
    try {
      const [focusRes, processRes, pageDataRes] = await Promise.all([
        servicesApi.getConsultingFocusAreas(),
        servicesApi.getConsultingProcess(),
        pagesApi.getByPageType("consulting"),
      ]);

      setFocusAreas(focusRes.data || []);
      setProcessSteps(processRes.data || []);
      setPageData(pageDataRes.data || []);
    } catch (error) {
      console.error("Error loading consulting data:", error);
      setFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const setFallbackData = () => {
    setFocusAreas([
      {
        title: "Business Model Innovation",
        description: "Develop and validate sustainable business models",
        icon: "💡",
      },
      {
        title: "Growth Strategy",
        description: "Create data-driven strategies for scalable growth",
        icon: "📈",
      },
      {
        title: "Digital Transformation",
        description: "Leverage technology to transform your business",
        icon: "🚀",
      },
      {
        title: "Fundraising Strategy",
        description: "Prepare and execute successful fundraising rounds",
        icon: "💰",
      },
    ]);

    setProcessSteps([
      {
        step_number: 1,
        title: "Discovery",
        description: "Understand your business, goals, and challenges",
      },
      {
        step_number: 2,
        title: "Strategy Development",
        description: "Create customized solutions and action plans",
      },
      {
        step_number: 3,
        title: "Implementation",
        description: "Execute strategies with measurable milestones",
      },
      {
        step_number: 4,
        title: "Review & Optimize",
        description: "Continuous improvement and performance tracking",
      },
    ]);
  };

  useEffect(() => {
    if (pageData?.use_slider && pageData?.slider_images?.length > 0) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % pageData.slider_images.length);
      }, pageData.slider_interval || 5000);
      return () => clearInterval(interval);
    }
  }, [pageData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <HeroSection
        title={
          pageData?.hero_headline ||
          pageData?.title ||
          "Become a better leader, transform your business"
        }
        subtitle={
          pageData?.hero_subheadline ||
          pageData?.meta_description ||
          pageData?.content ||
          "Turn people into assets, drive growth with innovation"
        }
        heroImage={pageData?.hero_image}
        useSlider={!!pageData?.use_slider}
        sliderImages={pageData?.slider_images || []}
        currentSlide={currentSlide}
        onSlideChange={setCurrentSlide}
        ctaText={pageData?.cta_text}
        ctaHref={pageData?.cta_link || "/contact"}
      />

      {/* Credibility */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-blue-800 mb-4">
              Expertise Backed by Experience
            </h2>
            <p className="text-blue-700 mb-4">
              Led by Mohammad Abdul Matin Emon, former Head of Growth at
              Doctorola and Head of Digital at Praava Health, our consulting
              brings real-world experience from scaling healthcare technology
              companies in Bangladesh.
            </p>
            <p className="text-blue-700">
              We combine strategic thinking with practical execution to deliver
              measurable results.
            </p>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Focus Areas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {focusAreas.map((area, index) => (
              <Link
                key={index}
                to={`/consulting/focus-area/${area.id}`}
                className="bg-white rounded-lg shadow p-6 text-center hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <div className="text-4xl mb-4">{area.icon}</div>
                <h3 className="text-xl font-bold mb-3">{area.title}</h3>
                <p className="text-gray-600 mb-4">{area.description}</p>
                <div className="text-blue-600 font-semibold text-sm">
                  Learn More →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Consulting Process
          </h2>
          <div className="bg-white rounded-xl shadow p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xl font-bold mb-4 mx-auto">
                    {step.step_number}
                  </div>
                  <h3 className="font-bold mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="mb-8 text-blue-100">
            Let's work together to create a strategy that drives real results.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
          >
            Book Your Strategy Session
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ConsultingPage;
